import { Text, useBreakpoint, useToast } from '@vtex/admin-ui'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory } from 'react-router'
import * as CustomerActions from '@/checkout-instore-legacy/customer/actions'
import { useDispatch } from 'react-redux'

import { DEFAULT_GROUP_PACKAGE_OPTIONS } from '../../../../core/constants/defaultGroupPackageOptions'
import { useIsMounted } from '../../../../core/hooks/useIsMounted'
import { useOrderForm } from '../../../../core/requests/checkout'
import type { Package } from '../../../../core/typings'
import { getOrderAsPackages } from '../../../../utils/product'
import type { StepProps } from '../../components'
import { Step, StepOrderSummary, StepSubmitButton } from '../../components'
import { useCheckout } from '../../state'
import { getClientDataStateFromOrderForm } from '../../utils/getClientDataStateFromOrderForm'
import { OrderFormLastUpdateLabel, ClientProfileSection } from './components'
import { PackageSection } from './components/PackageSection'
import { TotalOrderSection } from './components/TotalOrderSection/TotalOrderSection'
import { TopbarMenuArea } from '../../../Instore/components/Header/Topbar'
import { BackButton } from '../../../../core/components'
import { getSelectedAddressesByShippingDataAddress } from '../../utils/getSelectedAddressesByShippingDataAddress'
import { getFirstAndLastNames } from '../../../../utils/names'

export type OrderSummaryScreenProps = StepProps

export const OrderSummaryScreen = ({ ...props }: OrderSummaryScreenProps) => {
  const { breakpoint } = useBreakpoint()
  const dispatch = useDispatch()

  const { orderForm, saveClientProfileData, setShippingData } = useOrderForm({
    swrConfig: {
      revalidateOnMount: true,
    },
  })

  const {
    setClientDataState,
    checkout: { clientData },
  } = useCheckout()

  const history = useHistory()
  const [isBusy, setIsBusy] = useState(false)
  const isMountedRef = useIsMounted()
  const showToast = useToast()
  const { formatMessage } = useIntl()

  if (!orderForm) {
    return null
  }

  const packages = getOrderAsPackages(orderForm, DEFAULT_GROUP_PACKAGE_OPTIONS)

  const handleStepSubmit = async () => {
    setIsBusy(true)

    const [firstName, lastName] = getFirstAndLastNames(clientData.name)

    try {
      await saveClientProfileData({
        document: clientData.document,
        documentType: orderForm.clientProfileData?.documentType,
        email: clientData.email,
        firstName,
        lastName,
        homePhone: clientData.phone,
        phone: clientData.phone,
        isCorporate: clientData.isCorporate,
        corporateName: clientData.companyInformation.companyName,
        tradeName: clientData.companyInformation.tradeName,
        corporatePhone: clientData.companyInformation.corporatePhone,
        corporateDocument: clientData.companyInformation.corporateDocument,
        stateInscription: clientData.companyInformation.stateInscription,
      })

      const selectedAddresses = getSelectedAddressesByShippingDataAddress({
        orderForm,
        clientData,
      })

      const newOrderForm = await setShippingData({ selectedAddresses })

      dispatch(CustomerActions.updateCustomer(newOrderForm.clientProfileData))
      setClientDataState(getClientDataStateFromOrderForm(newOrderForm))

      history.push('/payment')
    } catch {
      showToast({
        message: formatMessage({
          id: 'errorSavingData',
        }),
        variant: 'critical',
        dismissible: true,
      })
    } finally {
      if (isMountedRef.current) {
        setIsBusy(false)
      }
    }
  }

  return (
    <Step {...props} data-testid="order-summary">
      <TopbarMenuArea>
        <BackButton />
      </TopbarMenuArea>
      <Text variant="display" aria-label="Title">
        <FormattedMessage id="checkout.order-summary.title" />
      </Text>
      <OrderFormLastUpdateLabel orderForm={orderForm} />
      <ClientProfileSection clientProfileData={clientData} />
      {packages.map((productPackage: Package, index) => (
        <PackageSection
          key={index + 1}
          packageNumber={index + 1}
          productPackage={productPackage}
        />
      ))}
      {breakpoint === 'mobile' && <TotalOrderSection />}
      <StepOrderSummary>
        <StepSubmitButton
          loading={isBusy}
          disabled={isBusy}
          onClick={handleStepSubmit}
          data-testid="button-payment"
        >
          <FormattedMessage id="checkout.order-summary.primary-action" />
        </StepSubmitButton>
      </StepOrderSummary>
    </Step>
  )
}
